// Cobertura do saldo de créditos da UC — quantos meses de consumo o estoque de
// crédito parado na distribuidora ainda paga.
//
// Regra (solicitação do CEO, 27/08/2026): a faixa saudável vai de 1 a 2 meses de
// cobertura. Abaixo de 1 mês a UC entra no próximo ciclo sem lastro, e o
// Analista precisa enxergar isso ANTES de fechar o rateio. Não existe ação
// automática — o alerta é visual e a decisão é caso a caso ("é necessário
// entender cada situação em particular").
//
// ⚠️ NÃO confundir com `avaliarTravamento` (src/lib/consumoOperacional.ts), que é
// o lado OPOSTO da mesma conta: cobertura ACIMA de 3 meses pausa a UC por
// EXCESSO de saldo (RAT-R11). Aquele limiar continua em 3 e este módulo não o
// toca: aqui nada trava, nada pausa e nada é escrito no banco.
//
// ⚠️ O saldo daqui é `faturas.saldo_ugs_vigor` — o "SALDO KWH" impresso no bloco
// SCEE, que é o ESTOQUE de créditos da UC. Não é `saldo_acumulado_kwh`, que
// apesar do nome é o resultado DO MÊS (excedente recebido − crédito recebido) e
// por construção raramente chega perto de um mês de consumo. Medir cobertura por
// ele acenderia o alerta na carteira inteira — levantamento em produção
// (28/08/2026): 21 de 21 UCs vinculadas com fatura ficariam abaixo de 1 mês. Com
// o estoque, o retrato separa: 11 abaixo de 1 mês, 5 dentro da faixa e 5 acima
// de 3.
//
//   node --test views/Gestao/Rateio/__tests__/coberturaSaldo.test.ts

import { consumoOperacionalDaUc, type UcComConsumo } from './consumoDaUc.ts';
import type { FonteConsumo } from './consumoOperacional.ts';

/**
 * O que este módulo precisa da última fatura da UC. É a fatia usada de
 * `FaturaConsumoResumo` (views/Gestao/Rateio/consumoHistorico.ts), declarada
 * aqui de forma estrutural: aquele arquivo importa o cliente Supabase do
 * navegador, que não existe no Deno nem faz sentido dentro de um teste puro.
 */
export type UltimaFaturaDaUc = {
  mes_referencia?: string | null;
  /** `faturas.saldo_ugs_vigor` — o ESTOQUE de créditos, não o saldo do mês. */
  saldo_creditos_kwh: number | null;
};

/** Abaixo disto o alerta acende. */
export const COBERTURA_MINIMA_SAUDAVEL_MESES = 1;
/** Teto da faixa saudável — informativo, não dispara nada por si só. */
export const COBERTURA_MAXIMA_SAUDAVEL_MESES = 2;

/**
 * Status da planilha do rateio para contrato ativo que está FORA do rateio: a UC
 * tem saldo para 2 meses ou mais, então não recebe nova injeção até gastar.
 */
export const STATUS_FORA_DO_RATEIO = 'Ativa-S/-Rateio';

/** Mesma grafia que `sync_uc_from_rateio` aceita para "Ativa-S/-Rateio". */
export function ehStatusForaDoRateio(status: unknown): boolean {
  return ['ativa-s/-rateio', 'ativa s/ rateio', 'ativa sem rateio', 'ativa-s-rateio'].includes(
    String(status ?? '').trim().toLowerCase()
  );
}

/**
 * Abaixo disto uma UC fora do rateio precisa VOLTAR a ele (#6190, 14/10/2026).
 *
 * É a mesma régua que a tirou do rateio (saldo para 2 meses ou mais), e não o
 * 1 mês do alerta de saldo baixo: a mudança enviada à distribuidora só vale no
 * ciclo seguinte. Avisando com menos de 1 mês, a UC já zera o saldo antes da
 * injeção voltar — e mês sem compensação é fatura sem cobrança da Vigor.
 */
export const COBERTURA_REINCLUIR_MESES = 2;

export function deveReincluirNoRateio(coberturaMeses: number | null | undefined): boolean {
  return coberturaMeses != null && Number.isFinite(coberturaMeses) && coberturaMeses < COBERTURA_REINCLUIR_MESES;
}

/**
 * Situações em que a UC não recebe mais injeção de crédito.
 *
 * Exclusão validada com o CEO: saldo baixo numa UC cancelada ou pausada há tempo
 * não é problema — ela já saiu do rateio. Alertar sobre isso só encheria a tela
 * de linha que o Analista não tem o que fazer a respeito. A grade do simulador
 * já filtra as duas antes de chegar aqui; a lista existe para a regra viajar
 * junto do alerta e não depender de quem chama.
 */
export const SITUACOES_SEM_INJECAO = ['Cancelada', 'Pausada'];

export type CoberturaSaldo = {
  ucId: number;
  /** `saldo_ugs_vigor` da fatura mais recente — o estoque de créditos. */
  saldoCreditosKwh: number;
  /** Consumo operacional resolvido (observado ou estimativa cadastral). */
  consumoBaseKwh: number;
  coberturaMeses: number;
  /** O alerta: cobertura abaixo de 1 mês. */
  abaixoDoMinimo: boolean;
  /** Cobertura dentro de 1 a 2 meses — a faixa que o CEO chamou de saudável. */
  dentroDaFaixa: boolean;
  fonteConsumo: FonteConsumo;
  /**
   * Consumo marcado como instável (meses que discordam demais entre si). O
   * alerta continua saindo — ele não age sozinho —, mas a tela precisa dizer que
   * o divisor é frágil antes do analista decidir.
   */
  consumoSuspeito: boolean;
  /** Mês da fatura de onde o saldo veio: o retrato pode não ser do mês corrente. */
  mesReferencia: string | null;
};

export type EntradaCobertura = {
  uc: UcComConsumo & { id: number; situacao?: string | null };
  /** `null`/`undefined` = a fatura não trouxe saldo. Ausência de dado não é alerta. */
  saldoCreditosKwh: number | null | undefined;
  mesReferencia?: string | null;
};

/**
 * Cobertura de uma UC, ou `null` quando a pergunta não se aplica.
 *
 * Devolver `null` em vez de zero é deliberado: "não sei o saldo desta UC" e
 * "esta UC está sem saldo" levam a ações opostas, e um zero fabricado apagaria
 * a diferença justamente onde ela decide o que o analista faz.
 */
export function avaliarCoberturaSaldo(entrada: EntradaCobertura, hoje?: Date): CoberturaSaldo | null {
  const { uc, saldoCreditosKwh, mesReferencia } = entrada;

  if (SITUACOES_SEM_INJECAO.includes((uc.situacao || '').trim())) return null;

  if (saldoCreditosKwh == null) return null;
  const saldo = Number(saldoCreditosKwh);
  if (!Number.isFinite(saldo)) return null;

  const consumo = consumoOperacionalDaUc(uc, hoje);
  const base = consumo.valorKwh;
  if (base == null || base <= 0) return null;

  const coberturaMeses = Math.round((saldo / base) * 100) / 100;

  return {
    ucId: uc.id,
    saldoCreditosKwh: saldo,
    consumoBaseKwh: base,
    coberturaMeses,
    abaixoDoMinimo: coberturaMeses < COBERTURA_MINIMA_SAUDAVEL_MESES,
    dentroDaFaixa:
      coberturaMeses >= COBERTURA_MINIMA_SAUDAVEL_MESES && coberturaMeses <= COBERTURA_MAXIMA_SAUDAVEL_MESES,
    fonteConsumo: consumo.fonte,
    consumoSuspeito: consumo.suspeito,
    mesReferencia: mesReferencia ?? null,
  };
}

/**
 * Cobertura de todas as UCs da grade, a partir do histórico de faturas que a
 * tela já carregou em lote. Usa a fatura mais recente de cada UC — o histórico
 * chega ordenado do mês mais novo para o mais antigo por `buscarHistoricoConsumoUcs`.
 *
 * UCs sem entrada no mapa são as que não dá para medir (sem fatura, sem consumo
 * ou fora do rateio) — nunca são alerta.
 */
export function mapearCoberturaSaldo(
  ucs: (UcComConsumo & { id: number; situacao?: string | null })[],
  historicoPorUc: Map<number, UltimaFaturaDaUc[]>,
  hoje?: Date
): Map<number, CoberturaSaldo> {
  const mapa = new Map<number, CoberturaSaldo>();
  for (const uc of ucs) {
    const ultima = (historicoPorUc.get(uc.id) || [])[0];
    const avaliacao = avaliarCoberturaSaldo(
      { uc, saldoCreditosKwh: ultima?.saldo_creditos_kwh, mesReferencia: ultima?.mes_referencia ?? null },
      hoje
    );
    if (avaliacao) mapa.set(uc.id, avaliacao);
  }
  return mapa;
}

/** Só as UCs que acendem o alerta — o que o banner conta. */
export function detectarSaldoBaixo(coberturas: Map<number, CoberturaSaldo>): CoberturaSaldo[] {
  return Array.from(coberturas.values()).filter(c => c.abaixoDoMinimo);
}
